import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchApkReleases,
  uploadApkRelease,
  setApkReleaseActive,
  deleteApkRelease,
  downloadApkRelease,
} from '../services/apkReleaseService';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';
  return error?.message || error?.data?.mesaj || error?.data?.message || fallback;
};

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const ApkReleasesPage = () => {
  const { confirm } = useConfirmDialog();
  const fileInputRef = useRef(null);

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);
  const [versionCode, setVersionCode] = useState('');
  const [versionName, setVersionName] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchApkReleases();
      setReleases(res?.veri?.surumler || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Sürümler yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.apk')) {
      window.showNotification?.('error', 'Yalnızca .apk dosyası seçilebilir');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    const code = parseInt(versionCode, 10);
    if (!Number.isFinite(code) || code < 1) {
      window.showNotification?.('error', 'versionCode pozitif bir tam sayı olmalıdır');
      return;
    }
    if (!versionName.trim()) {
      window.showNotification?.('error', 'Sürüm adı (versionName) zorunludur');
      return;
    }
    if (!selectedFile) {
      window.showNotification?.('error', 'APK dosyası seçin');
      return;
    }

    setUploading(true);
    try {
      await uploadApkRelease({
        file: selectedFile,
        versionCode: code,
        versionName: versionName.trim(),
        releaseNotes: releaseNotes.trim(),
        forceUpdate,
      });
      window.showNotification?.('success', 'APK sürümü yüklendi');
      setVersionCode('');
      setVersionName('');
      setReleaseNotes('');
      setForceUpdate(false);
      setSelectedFile(null);
      await loadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'APK yüklenemedi'));
    } finally {
      setUploading(false);
    }
  };

  const handleToggleActive = async (release) => {
    setActionId(release.id);
    try {
      await setApkReleaseActive(release.id, !release.isActive);
      window.showNotification?.('success', release.isActive ? 'Sürüm pasifleştirildi' : 'Sürüm aktifleştirildi');
      await loadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Durum güncellenemedi'));
    } finally {
      setActionId(null);
    }
  };

  const handleDownload = async (release) => {
    setActionId(release.id);
    try {
      const name =
        release.originalFilename ||
        `unmak-mobile-v${release.versionName}-${release.versionCode}.apk`;
      await downloadApkRelease(release.id, name);
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'APK indirilemedi'));
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (release) => {
    const ok = await confirm({
      title: 'APK sürümünü sil',
      message: `v${release.versionName} (${release.versionCode}) kalıcı olarak silinsin mi?`,
      variant: 'danger',
      confirmLabel: 'Sil',
    });
    if (!ok) return;

    setActionId(release.id);
    try {
      await deleteApkRelease(release.id);
      window.showNotification?.('success', 'APK sürümü silindi');
      await loadAll();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'APK silinemedi'));
    } finally {
      setActionId(null);
    }
  };

  const creatorName = (release) => {
    const u = release.createdBy;
    if (!u) return '—';
    const full = `${u.ad || ''} ${u.soyad || ''}`.trim();
    return full || u.kullaniciAdi || '—';
  };

  return (
    <div className="page-container apk-releases-page">
      <div className="page-header">
        <h1>APK Sürümleri</h1>
      </div>

      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '16px',
          }}
        >
          {error}
        </div>
      )}

      <section
        style={{
          background: '#fff',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px',
        }}
      >
        <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>Yeni APK Yükle</h2>
        <p style={{ color: 'var(--text-gray)', fontSize: '14px', marginBottom: '16px' }}>
          versionCode her sürümde artmalıdır (Android integer sürüm). versionName kullanıcıya gösterilen semver metnidir (örn. 1.2.0).
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <input
            type="number"
            min="1"
            placeholder="versionCode (örn. 2)"
            value={versionCode}
            onChange={(e) => setVersionCode(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: '8px',
            }}
          />
          <input
            type="text"
            placeholder="versionName (örn. 1.1.0)"
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            maxLength={50}
            style={{
              padding: '10px 12px',
              border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: '8px',
            }}
          />
        </div>

        <textarea
          placeholder="Sürüm notları (opsiyonel)"
          value={releaseNotes}
          onChange={(e) => setReleaseNotes(e.target.value)}
          maxLength={5000}
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '8px',
            marginBottom: '12px',
            resize: 'vertical',
          }}
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '14px' }}>
          <input
            type="checkbox"
            checked={forceUpdate}
            onChange={(e) => setForceUpdate(e.target.checked)}
          />
          Zorunlu güncelleme (kullanıcı atlayamaz)
        </label>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <button type="button" className="secondary-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            APK Seç
          </button>
          {selectedFile && (
            <span style={{ fontSize: '14px', color: 'var(--text-gray)' }}>
              {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </span>
          )}
          <button type="button" className="primary-btn" onClick={handleUpload} disabled={uploading}>
            {uploading ? 'Yükleniyor…' : 'Yükle'}
          </button>
        </div>
      </section>

      <section
        style={{
          background: '#fff',
          border: '1px solid var(--border-color, #e2e8f0)',
          borderRadius: '8px',
          padding: '20px',
        }}
      >
        <h2 style={{ fontSize: '18px', marginBottom: '16px' }}>Kayıtlı Sürümler</h2>

        {loading ? (
          <div style={{ padding: '20px', color: '#64748b' }}>Yükleniyor…</div>
        ) : releases.length === 0 ? (
          <div style={{ padding: '20px', color: '#64748b' }}>Henüz APK sürümü yok.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', minWidth: '720px' }}>
              <thead>
                <tr>
                  <th>versionCode</th>
                  <th>versionName</th>
                  <th>Durum</th>
                  <th>Boyut</th>
                  <th>Zorunlu</th>
                  <th>Oluşturan</th>
                  <th>Tarih</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {releases.map((release) => (
                  <tr key={release.id}>
                    <td>{release.versionCode}</td>
                    <td>{release.versionName}</td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          background: release.isActive ? '#dcfce7' : '#f1f5f9',
                          color: release.isActive ? '#166534' : '#64748b',
                        }}
                      >
                        {release.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>{formatFileSize(release.size)}</td>
                    <td>{release.forceUpdate ? 'Evet' : 'Hayır'}</td>
                    <td>{creatorName(release)}</td>
                    <td>{formatDate(release.createdAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="secondary-btn"
                          disabled={actionId === release.id}
                          onClick={() => handleDownload(release)}
                        >
                          İndir
                        </button>
                        <button
                          type="button"
                          className="secondary-btn"
                          disabled={actionId === release.id}
                          onClick={() => handleToggleActive(release)}
                        >
                          {release.isActive ? 'Pasifleştir' : 'Aktifleştir'}
                        </button>
                        <button
                          type="button"
                          className="danger-btn"
                          disabled={actionId === release.id}
                          onClick={() => handleDelete(release)}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ApkReleasesPage;
